import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CardConfiguration } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Organisation } from 'src/app/global/global.model';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';
@Component({
    selector: 'app-config-cards',
    templateUrl: './config-cards.component.html',
    styleUrls: ['./config-cards.component.scss'],
})
export class ConfigCardsComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    loading = false;
    processingSubmit = false;
    orgConfig: any;
    org: Organisation;
    currentCardConfig: CardConfiguration;
    currentDescription: any;
    cardConfigForm = new FormGroup({});
    availableInFrench: boolean;
    availableInEnglish: boolean;
    badges = [];
    langs: string[];
    availableTextConfigs = [
        'instructionsGeneralIntroduction',
        'instructionsStepsInfo',
        'instructionsStepsAttachments',
        'instructionsStepsConfirmation',
        'tooltipSubscribe',
        'tooltipLink',
        'approvalGuidelines',
    ];
    autoApproveCard = false;

    get currentLang() {
        return this.translate.currentLang;
    }

    constructor(
        public translate: TranslateService,
        private configAllService: ConfigAllService,
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.loading = true;

        this.contextService
            .getLanguage()
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translate.use(lang)),
            )
            .subscribe();

        combineLatest([this.contextService.getOrganisationObs(), this.contextService.getAvailableLanguages(), this.contextService.getOrganisationConfiguration()])
            .pipe(
                takeUntil(this.destroy$),
                filter(([org, langs, config]) => !!org && !!langs && !!config),
                map(([org, langs, config]) => {
                    this.org = org;
                    this.langs = langs;
                    this.orgConfig = config;

                    langs.forEach((lang) => {
                        this.availableTextConfigs.forEach((availableTextConfig) => {
                            this.cardConfigForm.addControl(`${lang}-${availableTextConfig}`, new FormControl());
                        });
                    });

                    this.cardConfigForm.addControl('proofOfIdentityCount', new FormControl('', [Validators.required, Validators.pattern('^[0-9]0?$'), Validators.max(10)]));
                    this.cardConfigForm.addControl('displayExpirationDate', new FormControl(null));

                    this.cdr.detectChanges();
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refreshOrgConfig() {
        this.contextService
            .refreshOrganisationConfiguration()
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.loading = false)),
            )
            .subscribe();
    }

    public cancel() {
        let instructionsGeneralIntroduction;
        let instructionsStepsInfo;
        let instructionsStepsAttachments;
        let instructionsStepsConfirmation;
        let tooltipSubscribe;
        let tooltipLink;

        this.cardConfigForm.controls['proofOfIdentityCount'].setValue(this.currentCardConfig?.proofOfIdentityCount);

        // Do a ?? to force true value when not found on DB bec this prop is newer
        this.cardConfigForm.controls['displayExpirationDate'].setValue(this.currentCardConfig?.displayExpirationDate ?? true);

        for (let lang of this.langs) {
            if (this.currentCardConfig?.identity?.instructions?.general?.introduction) {
                instructionsGeneralIntroduction = this.currentCardConfig?.identity?.instructions?.general?.introduction[lang] || '';
                this.cardConfigForm.controls[`${lang}-instructionsGeneralIntroduction`].setValue(instructionsGeneralIntroduction);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsGeneralIntroduction`].setValue('');
            }

            if (this.currentCardConfig?.identity?.instructions?.steps?.info) {
                instructionsStepsInfo = this.currentCardConfig?.identity?.instructions?.steps?.info[lang] || '';
                this.cardConfigForm.controls[`${lang}-instructionsStepsInfo`].setValue(instructionsStepsInfo);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsStepsInfo`].setValue('');
            }

            if (this.currentCardConfig?.identity?.instructions?.steps?.attachments) {
                instructionsStepsAttachments = this.currentCardConfig?.identity?.instructions?.steps?.attachments[lang] || '';
                this.cardConfigForm.controls[`${lang}-instructionsStepsAttachments`].setValue(instructionsStepsAttachments);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsStepsAttachments`].setValue('');
            }

            if (this.currentCardConfig?.identity?.instructions?.steps?.confirmation) {
                instructionsStepsConfirmation = this.currentCardConfig?.identity?.instructions?.steps?.confirmation[lang] || '';
                this.cardConfigForm.controls[`${lang}-instructionsStepsConfirmation`].setValue(instructionsStepsConfirmation);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsStepsConfirmation`].setValue('');
            }

            if (this.currentCardConfig?.tooltips?.subscribe) {
                tooltipSubscribe = this.currentCardConfig?.tooltips?.subscribe[lang] || '';
                this.cardConfigForm.controls[`${lang}-tooltipSubscribe`].setValue(tooltipSubscribe);
            } else {
                this.cardConfigForm.controls[`${lang}-tooltipSubscribe`].setValue('');
            }
            if (this.currentCardConfig.linkable) {
                if (this.currentCardConfig?.tooltips?.link) {
                    tooltipLink = this.currentCardConfig?.tooltips?.link[lang] || '';
                    this.cardConfigForm.controls[`${lang}-tooltipLink`].setValue(tooltipLink);
                } else {
                    this.cardConfigForm.controls[`${lang}-tooltipLink`].setValue('');
                }
            }

            if ((this.currentCardConfig as any)?.approvalGuidelines) {
                this.cardConfigForm.controls[`${lang}-approvalGuidelines`].setValue((this.currentCardConfig as any).approvalGuidelines[lang] || '');
            } else {
                this.cardConfigForm.controls[`${lang}-approvalGuidelines`].setValue('');
            }
        }
        this.contextService.successMessage(this.translate.instant('pages.config-all.config-cards.messages.cancelled'));
    }

    public submit() {
        this.processingSubmit = true;
        let orgSupport = {
            cards: this.orgConfig.support.cards.slice(),
        };
        let cardPosition = this.findCardConfigIndex(this.currentCardConfig.type);

        this.currentCardConfig = {
            ...this.currentCardConfig,
            identity: {
                label: this.currentCardConfig.identity.label,
                icon: this.currentCardConfig.identity.icon,
                instructions: {
                    general: {
                        introduction: {},
                    },
                    steps: {
                        info: {},
                        attachments: {},
                        confirmation: {},
                    },
                },
            },
            tooltips: { subscribe: {}, link: {} },
        };
        (this.currentCardConfig as any).approvalGuidelines = {};

        this.currentCardConfig.proofOfIdentityCount = +this.cardConfigForm.controls['proofOfIdentityCount'].value;

        // Do a ?? to force true value when not found on DB bec this prop is newer
        this.currentCardConfig.displayExpirationDate = this.cardConfigForm.controls['displayExpirationDate'].value ?? true;
        for (let lang of this.langs) {
            this.currentCardConfig.identity.instructions.general.introduction[lang] = this.cardConfigForm.controls[`${lang}-instructionsGeneralIntroduction`].value;
            this.currentCardConfig.identity.instructions.steps.info[lang] = this.cardConfigForm.controls[`${lang}-instructionsStepsInfo`].value;
            this.currentCardConfig.identity.instructions.steps.attachments[lang] = this.cardConfigForm.controls[`${lang}-instructionsStepsAttachments`].value;
            this.currentCardConfig.identity.instructions.steps.confirmation[lang] = this.cardConfigForm.controls[`${lang}-instructionsStepsConfirmation`].value;
            this.currentCardConfig.tooltips.subscribe[lang] = this.cardConfigForm.controls[`${lang}-tooltipSubscribe`].value;
            if (this.currentCardConfig.linkable) {
                this.currentCardConfig.tooltips.link[lang] = this.cardConfigForm.controls[`${lang}-tooltipLink`].value;
            }
            (this.currentCardConfig as any).approvalGuidelines[lang] = this.cardConfigForm.controls[`${lang}-approvalGuidelines`].value;
        }

        if (cardPosition != -1) {
            orgSupport.cards[cardPosition] = this.currentCardConfig;
            const payload: PatchOrganisationConfiguration = {
                group: 'support',
                data: orgSupport,
            };
            this.configAllService
                .patchOrganisationConfiguration(payload, this.org.tag)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (response) => {
                        this.orgConfig = response;
                        this.processingSubmit = false;
                        this.contextService.successMessage(this.translate.instant('pages.config-all.config-cards.messages.success'));
                        this.refreshOrgConfig();
                    },
                    (error) => {
                        this.processingSubmit = false;
                        this.contextService.errorMessage(this.translate.instant('pages.config-all.config-cards.messages.error'));
                        console.log(error);
                    },
                );
        } else {
            this.processingSubmit = false;
            this.contextService.errorMessage(this.translate.instant('pages.config-all.config-cards.messages.error'));
            console.log('Couldnt find the card config in the organisation config');
        }
    }

    public cardChange(cardConfig: any) {
        this.currentCardConfig = cardConfig;
        if (!this.currentCardConfig.linkable) {
            this.availableTextConfigs = this.availableTextConfigs.filter((s) => s !== 'tooltipLink');
        }

        this.cardConfigForm.controls['proofOfIdentityCount'].setValue(this.currentCardConfig?.proofOfIdentityCount);

        // Do a ?? to force true value when not found on DB bec this prop is newer
        this.cardConfigForm.controls['displayExpirationDate'].setValue(this.currentCardConfig?.displayExpirationDate ?? true);

        this.autoApproveCard = this.currentCardConfig?.autoApprove || false;

        for (let lang of this.langs) {
            if (this.currentCardConfig?.identity.instructions.general.introduction) {
                this.cardConfigForm.controls[`${lang}-instructionsGeneralIntroduction`].setValue(this.currentCardConfig?.identity.instructions.general.introduction[lang]);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsGeneralIntroduction`].setValue('');
            }
            if (this.currentCardConfig?.identity.instructions.steps.info) {
                this.cardConfigForm.controls[`${lang}-instructionsStepsInfo`].setValue(this.currentCardConfig?.identity.instructions.steps.info[lang]);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsStepsInfo`].setValue('');
            }
            if (this.currentCardConfig?.identity.instructions.steps.attachments) {
                this.cardConfigForm.controls[`${lang}-instructionsStepsAttachments`].setValue(this.currentCardConfig?.identity.instructions.steps.attachments[lang]);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsStepsAttachments`].setValue('');
            }
            if (this.currentCardConfig?.identity.instructions.steps.confirmation) {
                this.cardConfigForm.controls[`${lang}-instructionsStepsConfirmation`].setValue(this.currentCardConfig?.identity.instructions.steps.confirmation[lang]);
            } else {
                this.cardConfigForm.controls[`${lang}-instructionsStepsConfirmation`].setValue('');
            }
            if (this.currentCardConfig?.tooltips?.subscribe) {
                this.cardConfigForm.controls[`${lang}-tooltipSubscribe`].setValue(this.currentCardConfig?.tooltips?.subscribe[lang]);
            } else {
                this.cardConfigForm.controls[`${lang}-tooltipSubscribe`].setValue('');
            }
            if (this.currentCardConfig?.tooltips?.link && this.currentCardConfig.linkable) {
                this.cardConfigForm.controls[`${lang}-tooltipLink`].setValue(this.currentCardConfig?.tooltips?.link[lang]);
            } else {
                this.cardConfigForm.controls[`${lang}-tooltipLink`].setValue('');
            }
            if ((this.currentCardConfig as any)?.approvalGuidelines) {
                this.cardConfigForm.controls[`${lang}-approvalGuidelines`].setValue((this.currentCardConfig as any).approvalGuidelines[lang] || '');
            } else {
                this.cardConfigForm.controls[`${lang}-approvalGuidelines`].setValue('');
            }
        }
    }

    private findCardConfigIndex(type: string) {
        for (let i = 0; i < this.orgConfig.support.cards.length; i++) {
            if (this.orgConfig.support.cards[i].type === type) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Validate the form.
     */
    public validateForm() {
        return this.cardConfigForm.valid;
    }

    /**
     * GETTERS
     */
    get proofOfIdentityCount() {
        return this.cardConfigForm.get('proofOfIdentityCount');
    }
}
